import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import apiClient from '@/libs/apiClient'

// ----------------------
// Colors Thunks
// ----------------------
export const fetchColors = createAsyncThunk('settings/fetchColors', async (_, thunkAPI) => {
  try {
    const res = await apiClient.get('/admin/v1/colors')

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch colors')
  }
})

export const addColor = createAsyncThunk('settings/addColor', async (body: FormData, thunkAPI) => {
  try {
    const res = await apiClient.post('/admin/v1/colors/add', body)

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to add color')
  }
})

export const updateColor = createAsyncThunk(
  'settings/updateColor',
  async ({ id, body }: { id: string; body: FormData }, thunkAPI) => {
    try {
      const res = await apiClient.post(`/admin/v1/colors/${id}/edit`, body)

      return res.data.data
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to update color')
    }
  }
)

// ----------------------
// Sizes Thunks
// ----------------------
export const fetchSizes = createAsyncThunk('settings/fetchSizes', async (_, thunkAPI) => {
  try {
    const res = await apiClient.get('/admin/v1/sizes')

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch sizes')
  }
})

export const addSize = createAsyncThunk('settings/addSize', async (body: FormData, thunkAPI) => {
  try {
    const res = await apiClient.post('/admin/v1/sizes/add', body)

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to add size')
  }
})

export const updateSize = createAsyncThunk(
  'settings/updateSize',
  async ({ id, body }: { id: string; body: FormData }, thunkAPI) => {
    try {
      const res = await apiClient.post(`/admin/v1/sizes/${id}/edit`, body)

      return res.data.data
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to update size')
    }
  }
)

// ----------------------
// Volumes Thunks
// ----------------------
export const fetchVolumes = createAsyncThunk('settings/fetchVolumes', async (_, thunkAPI) => {
  try {
    const res = await apiClient.get('/admin/v1/volumes')

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch volumes')
  }
})

// ----------------------
// Weights Thunks
// ----------------------
export const fetchWeights = createAsyncThunk('settings/fetchWeights', async (_, thunkAPI) => {
  try {
    const res = await apiClient.get('/admin/v1/weights')

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch weights')
  }
})

// ----------------------
// Variant Options Thunk
// ----------------------
export const fetchVariantOptions = createAsyncThunk('settings/fetchVariantOptions', async (_, thunkAPI) => {
  try {
    const res = await apiClient.get('/admin/v1/settings/variant-options')

    return res.data.data
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch variant options')
  }
})

// -------------------------------------------------
// Slice
// -------------------------------------------------
type SettingsState = {
  colors: any[]
  sizes: any[]
  volumes: any[]
  weights: any[]
  loading: boolean
  error: string | null
}

const initialState: SettingsState = {
  colors: [],
  sizes: [],
  volumes: [],
  weights: [],
  loading: false,
  error: null
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // ------------------ COLORS ------------------
    builder
      .addCase(fetchColors.pending, state => {
        state.loading = true
      })
      .addCase(fetchColors.fulfilled, (state, action) => {
        state.loading = false
        state.colors = action.payload
      })
      .addCase(fetchColors.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        toast.error(state.error)
      })

      .addCase(addColor.fulfilled, (state, action) => {
        state.colors.unshift(action.payload)
        toast.success('Color added successfully')
      })
      .addCase(addColor.rejected, (_, action) => {
        toast.error(action.payload as string)
      })

      .addCase(updateColor.fulfilled, (state, action) => {
        const updated = action.payload

        state.colors = state.colors.map(c => (c.id === updated.id ? updated : c))
      })
      .addCase(updateColor.rejected, (_, action) => {
        toast.error(action.payload as string)
      })

    // ------------------ SIZES ------------------
    builder
      .addCase(fetchSizes.pending, state => {
        state.loading = true
      })
      .addCase(fetchSizes.fulfilled, (state, action) => {
        state.loading = false
        state.sizes = action.payload
      })
      .addCase(fetchSizes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        toast.error(state.error)
      })

      .addCase(addSize.fulfilled, (state, action) => {
        state.sizes.unshift(action.payload)
        toast.success('Size added')
      })
      .addCase(addSize.rejected, (_, action) => {
        toast.error(action.payload as string)
      })

      .addCase(updateSize.fulfilled, (state, action) => {
        const updated = action.payload

        state.sizes = state.sizes.map(s => (s.id === updated.id ? updated : s))
      })
      .addCase(updateSize.rejected, (_, action) => {
        toast.error(action.payload as string)
      })

    // ------------------ VOLUMES ------------------
    builder
      .addCase(fetchVolumes.pending, state => {
        state.loading = true
      })
      .addCase(fetchVolumes.fulfilled, (state, action) => {
        state.loading = false
        state.volumes = action.payload
      })
      .addCase(fetchVolumes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // ------------------ WEIGHTS ------------------
    builder
      .addCase(fetchWeights.pending, state => {
        state.loading = true
      })
      .addCase(fetchWeights.fulfilled, (state, action) => {
        state.loading = false
        state.weights = action.payload
      })
      .addCase(fetchWeights.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // ------------------ VARIANT OPTIONS ------------------
    builder
      .addCase(fetchVariantOptions.pending, state => {
        state.loading = true
      })
      .addCase(fetchVariantOptions.fulfilled, (state, action) => {
        state.loading = false
        const { colors, sizes, volumes, weights } = action.payload
        state.colors = colors || []
        state.sizes = sizes || []
        state.volumes = volumes || []
        state.weights = weights || []
      })
      .addCase(fetchVariantOptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export default settingsSlice.reducer
